import { useMemo, useState } from 'react'
import { Alert, Select } from 'antd'
import { sizes } from '@qflow/theme'

import {
  ApplyButtonContainer,
  StyledUpdateSection,
  TagInputContainer
} from 'portal/components/CommonStyles/ActionStripStyledComponents'
import { ButtonWithMessageContainer } from 'portal/components/CommonStyles/AssignmentStripStyledComponents'

import { useProjectTags } from 'portal/lib/hooks'
import { ColourButton, Text } from 'portal/lib/primitives'

type TaggingStripProps = {
  existingTags?: string[]
  onApply: (tags: string[]) => void
  recordCount: number
}

export function TaggingStrip({
  existingTags,
  onApply,
  recordCount
}: TaggingStripProps) {
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [searchValue, setSearchValue] = useState('')
  const { projectTags } = useProjectTags()

  // Include selected tags so newly entered tags remain visible in the dropdown
  const options = useMemo(() => {
    const tagsFromStore =
      projectTags.length > 0 ? projectTags : existingTags || []
    const allTags = [...new Set([...tagsFromStore, ...selectedTags])].sort(
      (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })
    )

    return allTags.map(tag => ({ value: tag, label: tag }))
  }, [existingTags, projectTags, selectedTags])

  const isApplyDisabled = selectedTags.length === 0

  const normalizeTags = (values: string[]) => {
    const sanitized = values
      .map(tag => tag.toLowerCase().trim().slice(0, 100))
      .filter(tag => tag.length > 0)

    return [...new Set(sanitized)]
  }

  const handleChange = (values: string[]) => {
    setSelectedTags(normalizeTags(values))
  }

  const handleSearch = (value: string) => {
    setSearchValue(value.toLowerCase())
  }

  const handleBlur = () => {
    setSelectedTags(normalizeTags(selectedTags))
  }

  const handleApply = () => {
    onApply(normalizeTags(selectedTags))
  }

  return (
    <StyledUpdateSection>
      <h3>Tag Records</h3>
      <TagInputContainer>
        <div style={{ margin: '0 0 0.5rem 0' }}>
          <Text size={sizes.SMALL_MEDIUM}>
            Type or select tags to apply to the selected records.
          </Text>
        </div>
        <Select
          mode="tags"
          style={{ width: '100%' }}
          placeholder="Type or select tags..."
          value={selectedTags}
          searchValue={searchValue}
          options={options}
          onChange={handleChange}
          onSearch={handleSearch}
          onSelect={val => {
            // Clear the search text after a tag is added
            setSearchValue('')
          }}
          onBlur={handleBlur}
          filterOption={(input, option) =>
            (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
          }
          notFoundContent={null}
        />
        <div style={{ margin: '0 0.25rem 0 0.25rem', textAlign: 'right' }}>
          <Text size={sizes.X_SMALL}>max. 100 characters per tag</Text>
        </div>
      </TagInputContainer>
      <Alert
        message="Top tip: After typing, press the Tab or Enter key to add the new tag."
        type="info"
        showIcon
        style={{
          padding: '0.5rem 0.5rem 0.5rem 0.75rem'
        }}
      />
      <ApplyButtonContainer>
        <ButtonWithMessageContainer style={{ marginTop: '0.5rem' }}>
          <ColourButton.Action
            type="button"
            onClick={handleApply}
            disabled={isApplyDisabled}
          >
            Apply to {recordCount} record{recordCount > 1 && 's'}
          </ColourButton.Action>
        </ButtonWithMessageContainer>
      </ApplyButtonContainer>
    </StyledUpdateSection>
  )
}
